import React, { useState, useCallback, useEffect } from 'react';
import { Row, Col, Typography } from 'antd';
import { InferenceFile, TableColumn } from '../../types';
import { SectionTitle } from '../../components/text/Titles';
import useGlobal from '../../store/Store';
import { InvoiceLineLabel } from './InvoiceLineLabel';
import { Shorten } from '../../components/text/Shorten';
import { useTranslation } from 'react-i18next';
const { Text } = Typography;

type Props = {
  inferenceFile: null | InferenceFile;
};

const valueStyle = {
  border: '1px solid #dedede',
  padding: '2px 6px',
  background: '#f2f2f2',
};

export const ResultTables = ({ inferenceFile }: Props) => {
  const { t } = useTranslation();
  const [, globalActions] = useGlobal();
  const [hover, setHover] = useState<{ table: number | null; index: number | null }>({ table: null, index: null });

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      globalActions.setLabelSelected(null);
    };
  }, [globalActions]);

  const onMouseEnter = useCallback((tableIndex: number, index: number) => {
    // For now, we'll just set the hover state without trying to convert to Label
    // This maintains the visual hover effect without the PDF highlighting
    setHover({ table: tableIndex, index });
  }, []);

  const onMouseLeave = useCallback(() => {
    globalActions.setLabelSelected(null);
    setHover({ table: null, index: null });
  }, [globalActions]);

  const getTableTitle = useCallback((tableName: string) => {
    if (!tableName) return 'Unknown Table';
    
    // Try to get a translation for the table name, fallback to a formatted version
    const translationKey = `sections.${tableName}`;
    const translation = t(translationKey, { defaultValue: null });
    
    if (translation && translation !== translationKey) {
      return translation;
    }
    
    // Format the table name for display
    return tableName
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  }, [t]);

  const getColumnLabel = useCallback((columnName: string) => {
    if (!columnName) return 'Unknown Column';
    
    // Try to get a translation for the column name
    const translationKey = `label.${columnName}`;
    const translation = t(translationKey, { defaultValue: null });
    
    if (translation && translation !== translationKey) {
      return translation;
    }
    
    // Format the column name for display
    return columnName
      .replace(/([A-Z])/g, ' $1')
      .replace(/\b\w/g, l => l.toUpperCase())
      .trim();
  }, [t]);

  const getSafeValue = useCallback((columnData: TableColumn | undefined): string => {
    if (!columnData?.positions?.[0]?.word) {
      return '';
    }
    
    const value = columnData.positions[0].word;
    return typeof value === 'string' ? value : String(value);
  }, []);

  const renderTableRow = useCallback((tableRow: { [key: string]: TableColumn }, tableIndex: number, index: number, columns: string[], totalRows: number) => {
    return (
      <div
        onMouseEnter={() => onMouseEnter(tableIndex, index)}
        onMouseLeave={onMouseLeave}
        key={`${tableIndex}-${index}`}
        className={hover.table === tableIndex && hover.index === index ? 'connectLineB' : ''}
        style={{
          background: '#f6f6f6',
          marginBottom: index + 1 === totalRows ? '0px' : '8px',
          padding: '8px',
        }}
        role="row"
        aria-label={`Table row ${index + 1}`}>
        <Row className="mb-1.5">
          <Col span={2} className="flex">
            <Text style={{ padding: '2px 2px' }}>#{index + 1}</Text>
          </Col>
          <Col span={22} className="flex">
            <div 
              style={{ 
                display: 'flex', 
                overflowX: 'auto', 
                gap: '12px',
                minWidth: '100%'
              }}
              role="grid"
              aria-label="Table columns">
              {columns.map((columnName, colIndex) => {
                const columnData = tableRow[columnName];
                const value = getSafeValue(columnData);
                
                if (!value) {
                  return null;
                }
                
                return (
                  <div 
                    key={`${columnName}-${colIndex}`} 
                    style={{ 
                      minWidth: '120px', 
                      flex: '0 0 auto',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                    role="gridcell"
                    aria-label={`${getColumnLabel(columnName)}: ${value}`}>
                    <InvoiceLineLabel>{getColumnLabel(columnName)}</InvoiceLineLabel>
                    <Text style={{
                      ...valueStyle,
                      wordBreak: 'break-word',
                      maxWidth: '120px'
                    }}>
                      {typeof value === 'string' && value.length > 20 ? (
                        <Shorten text={value} digits={20} />
                      ) : (
                        value
                      )}
                    </Text>
                  </div>
                );
              })}
            </div>
          </Col>
        </Row>
      </div>
    );
  }, [onMouseEnter, onMouseLeave, getSafeValue, getColumnLabel, hover]);

  // Early return for null/undefined inferenceFile
  if (!inferenceFile?.inferenceResult?.result?.tables) {
    return (
      <Col className="mb-1 px-4">
        <Text>{t('general.no-rows')}</Text>
      </Col>
    );
  }

  const tables = inferenceFile.inferenceResult.result.tables;

  return (
    <Col className="mb-1 px-4">
      <>
        {tables.length > 0 ? (
          tables.map((table, tableIndex) => {
            // Safely get columns
            const columns = table.header || (table.content?.[0] ? Object.keys(table.content[0]) : []);
            
            if (!table.content || table.content.length === 0) {
              return null;
            }
            
            return (
              <Row className="flex-col mb-6" key={table.name || `table-${tableIndex}`}>
                <SectionTitle fontSize={16}>
                  {getTableTitle(table.name || `table-${tableIndex}`)}
                </SectionTitle>
                <Col className="mt-2">
                  <div 
                    style={{ border: '1px solid #dfdfdf', padding: '8px', maxHeight: '350px', overflowY: 'auto' }}
                    role="table"
                    aria-label={`${getTableTitle(table.name || `table-${tableIndex}`)} table`}>
                    {table.content.map((tableRow: { [key: string]: TableColumn }, index) => 
                      renderTableRow(tableRow, tableIndex, index, columns, table.content.length)
                    )}
                  </div>
                </Col>
              </Row>
            );
          })
        ) : (
          <Col>
            <Text>{t('general.no-rows')}</Text>
          </Col>
        )}
      </>
    </Col>
  );
};
